// ================================================================
// src/pages/teacher/TeacherProjectEditor.jsx
// ================================================================
// ویرایشگر پروژه — موتور ساخت محتوا برای معلم
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. نمای فصل‌ها و درس‌ها (شبیه BookChapters اما ویرایش‌پذیر)
//   ۲. CRUD کامل روی فصل/درس/مرحله/ویدیو
//   ۳. Lesson Editor Modal تمام‌صفحه
//   ۴. Preview Mode (نمایش آنچه دانش‌آموز می‌بیند)
//   ۵. Change Indicator (مقایسه‌ی draft با published)
//   ۶. Submit for Review + Update Request Flow
//   ۷. AutoSave + Undo/Redo + Keyboard Shortcuts
//   ۸. Guard در حالت update-pending (قفل ویرایش)
//   ۹. Before Unload Warning
//  ۱۰. 🆕 تنظیمات اشتراک (قیمت + مدت + مهلت)
//  ۱۱. 🆕 علامت‌گذاری درس رایگان
// ================================================================

import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';

import {
  BooksEngineProvider,
  useBooksEngine, useEngineReady,
  useDraftEditor, useProject, useTeacherByUserId,
  useProjectVersions, useVersion,
  useVersionActions,
  useKeyboardShortcuts, useBeforeUnload,
  PROJECT_STATE, VERSION_STATUS,
  GRADE_TITLES, SUBJECT_LABELS,
} from '../../hooks/useBooksEngine.js';



import dataEngine from '../../core/books/data.js';
import { engine, uploadScope } from '../../core/books/storage.js';
import { diffEngine } from '../../core/books/logic.js';

import {
  Spinner, Badge, Chip, EmptyState, SkeletonLoader, LockBanner,
  AutoSaveIndicator, Modal, ConfirmModal,
  BlobImage, ImageUploader, VideoUploader, VideoPreview,
  VersionBadge, ProjectStateBadge, DiffViewer,
} from '../../components/books/BooksSharedUI.jsx';

import { StepTypeSelectorModal, QuizCreateModeModal } from '../../components/books/StepTypeSelector.jsx';
import '../../components/books/StepQuiz.css';

// ================================================================
// SECTION 1: ICONS
// ================================================================

const IconPlus = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconEdit = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconTrash = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconUp = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 14} height={p.size || 14} fill="none" {...p}>
    <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconDown = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 14} height={p.size || 14} fill="none" {...p}>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconChevron = ({ dir = 'down', size = 16 }) => {
  const paths = {
    down: 'M6 9l6 6 6-6',
    up: 'M6 15l6-6 6 6',
    left: 'M15 6l-6 6 6 6',
    right: 'M9 6l6 6-6 6',
  };
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path d={paths[dir]} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};
const IconEye = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);
const IconSend = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconSave = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconUndo = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M3 7v6h6M3 13a9 9 0 1015-6.7L21 9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconRedo = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M21 7v6h-6M21 13a9 9 0 10-15-6.7L3 9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconArrow = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 20} height={p.size || 20} fill="none" {...p}>
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconVideo = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="none" {...p}>
    <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v-4z" fill="currentColor" opacity="0.35"/>
    <rect x="3" y="6" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);

// ================================================================
// SECTION 2: MAIN ENTRY
// ================================================================

export default function TeacherProjectEditor() {
  const { projectId } = useParams();
  const { user } = useAuth();

  if (!user || user.role !== 'teacher') {
    return <AccessDenied />;
  }
  return (
    <BooksEngineProvider user={user}>
      <EditorInner projectId={projectId} />
    </BooksEngineProvider>
  );
}

function AccessDenied() {
  const navigate = useNavigate();
  return (
    <div className="tpe-denied">
      <div className="tpe-denied-card">
        <div className="tpe-denied-icon">🔒</div>
        <h2>دسترسی محدود</h2>
        <p>این صفحه مخصوص معلمان است.</p>
        <button className="tpe-btn tpe-btn-primary" onClick={() => navigate('/TeacherHome')}>
          بازگشت
        </button>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 3: EDITOR INNER
// ================================================================

function EditorInner({ projectId }) {
  const navigate = useNavigate();
  const { user, toast } = useBooksEngine();
  const { ready } = useEngineReady();

  const project = useProject(projectId);
  const teacherProfile = useTeacherByUserId(user?.id);
  const draft = useDraftEditor(projectId);

  // 🆕 ست کردن دامنه‌ی آپلود — همه‌ی تصاویر/ویدیوهای این پروژه در
  //     media/exams/{teacherId}/{projectId}/... ذخیره می‌شوند
  //     این کار باعث می‌شود هنگام حذف پروژه، همه‌ی فایل‌ها یک‌جا پاک شوند.
  useEffect(() => {
    if (!teacherProfile?.id || !project?.id) return;
    const prevOwner = uploadScope.ownerId;
    const prevExam = uploadScope.examId;
    uploadScope.ownerId = teacherProfile.id;
    uploadScope.examId = project.id;
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info('[uploadScope] set to', { ownerId: teacherProfile.id, examId: project.id });
    }
    return () => {
      uploadScope.ownerId = prevOwner;
      uploadScope.examId = prevExam;
    };
  }, [teacherProfile?.id, project?.id]);

  // ═══ همه‌ی useState ═══
  // 🆕 خواندن از engine (به‌جای localStorage مستقیم) تا هماهنگ با remoteSync باشه
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return engine.getRaw('navbar2-drawer-dark') === '1'; }
    catch { return false; }
  });
  const [diff, setDiff] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [openChapters, setOpenChapters] = useState({});
  const [editingLesson, setEditingLesson] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [addingChapter, setAddingChapter] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');

  // 🆕 Step Type و Quiz Mode
  const [stepTypeModal, setStepTypeModal] = useState(null);  // { chapterId, lessonId }
  const [quizModeModal, setQuizModeModal] = useState(null);  // { chapterId, lessonId, stepId }

  // 🆕 Modal نام درس
  const [addingLessonForChapter, setAddingLessonForChapter] = useState(null);
  const [newLessonTitle, setNewLessonTitle] = useState('');

  // 🆕 Modal نام مرحله (بعد از انتخاب نوع)
  const [stepNameModal, setStepNameModal] = useState(null); // { chapterId, lessonId, type }
  const [newStepTitle, setNewStepTitle] = useState('');

  // 🆕 تنظیمات اشتراک
  const [showSubscriptionSettings, setShowSubscriptionSettings] = useState(false);

  // ═══ useMemo ═══
  const isOwner = useMemo(() => {
    if (!project || !teacherProfile) return false;
    return project.teacherId === teacherProfile.id;
  }, [project, teacherProfile]);

  const shortcuts = useMemo(() => ({
    'ctrl+s': () => { if (draft.hasUnsaved) draft.forceSave(); },
    'mod+s': () => { if (draft.hasUnsaved) draft.forceSave(); },
    'ctrl+z': () => draft.canUndo && draft.undo(),
    'mod+z': () => draft.canUndo && draft.undo(),
    'ctrl+shift+z': () => draft.canRedo && draft.redo(),
    'mod+shift+z': () => draft.canRedo && draft.redo(),
    'ctrl+y': () => draft.canRedo && draft.redo(),
    'escape': () => {
      if (editingLesson) setEditingLesson(null);
      else if (showSubscriptionSettings) setShowSubscriptionSettings(false);
    },
  }), [draft, editingLesson, showSubscriptionSettings]);

  // ═══ همه‌ی useCallback — قبل از هر early return ═══
  const toggleChapter = useCallback((chapterId) => {
    setOpenChapters((prev) => ({ ...prev, [chapterId]: !prev[chapterId] }));
  }, []);

  const handleAddChapter = useCallback(() => {
    const title = newChapterTitle.trim() || 'فصل جدید';
    draft.addChapter(title);
    setNewChapterTitle('');
    setAddingChapter(false);
    toast?.('فصل جدید اضافه شد', 'success');
  }, [draft, newChapterTitle, toast]);

  const handleRenameChapter = useCallback((chapterId, title) => {
    draft.updateChapter(chapterId, { title });
  }, [draft]);

  const handleDeleteChapter = useCallback((chapter) => {
    setConfirmAction({
      type: 'delete-chapter',
      payload: { chapterId: chapter._id },
      message: `آیا از حذف "${chapter.title}" با ${chapter.lessons?.length || 0} درس مطمئن هستید؟`,
    });
  }, []);

  // 🆕 کلیک «افزودن درس» → باز کردن Modal نام درس
  const handleAddLesson = useCallback((chapterId) => {
    setAddingLessonForChapter(chapterId);
    setNewLessonTitle('');
  }, []);

  // 🆕 تایید از Modal → ساخت درس با نام دلخواه
  const confirmAddLesson = useCallback(() => {
    if (!addingLessonForChapter) return;
    const title = newLessonTitle.trim() || 'درس جدید';
    draft.addLesson(addingLessonForChapter, title);
    toast?.(`درس «${title}» اضافه شد — برای افزودن مرحله، روی کارت درس کلیک کنید`, 'success');
    setAddingLessonForChapter(null);
    setNewLessonTitle('');
  }, [draft, addingLessonForChapter, newLessonTitle, toast]);

  // 🆕 کلیک روی «افزودن مرحله» → انتخاب نوع
  const handleAddStepClick = useCallback((chapterId, lessonId) => {
    setStepTypeModal({ chapterId, lessonId });
  }, []);

  // 🆕 انتخاب نوع → باز کردن Modal نام مرحله (نه ساخت فوری)
  const handleStepTypeSelected = useCallback((type) => {
    if (!stepTypeModal) return;
    const { chapterId, lessonId } = stepTypeModal;
    setStepTypeModal(null);
    setStepNameModal({ chapterId, lessonId, type });
    setNewStepTitle(type === 'quiz' ? 'آزمون جدید' : 'مرحله جدید');
  }, [stepTypeModal]);

  // 🆕 تایید از Modal → ساخت مرحله با نام دلخواه
  const confirmAddStep = useCallback(() => {
    if (!stepNameModal) return;
    const { chapterId, lessonId, type } = stepNameModal;
    const title = newStepTitle.trim() || (type === 'quiz' ? 'آزمون جدید' : 'مرحله جدید');

    if (type === 'lesson') {
      draft.addStep(chapterId, lessonId, title, { type: 'lesson' });
      toast?.(`مرحله «${title}» اضافه شد`, 'success');
      setStepNameModal(null);
      setNewStepTitle('');
    } else {
      const newStepId = draft.addStep(chapterId, lessonId, title, {
        type: 'quiz',
        returnId: true,
      });
      if (!newStepId) {
        console.error('[TeacherProjectEditor] addStep failed');
        setStepNameModal(null);
        return;
      }
      setStepNameModal(null);
      setNewStepTitle('');
      setQuizModeModal({ chapterId, lessonId, stepId: newStepId });
    }
  }, [draft, stepNameModal, newStepTitle, toast]);

  const handleQuizModeSelected = useCallback(async (mode) => {
    if (!quizModeModal) return;
    const { chapterId, lessonId, stepId } = quizModeModal;

    setQuizModeModal(null);

    try {
      await draft.forceSave();
    } catch (e) {
      console.error('[TeacherProjectEditor] forceSave failed before navigate:', e);
    }

    const qs = new URLSearchParams({
      chapterId,
      lessonId,
      stepId,
      mode,
    }).toString();

    navigate(`/teacher/books/edit/${projectId}/quiz/new?${qs}`);
  }, [draft, quizModeModal, navigate, projectId]);

  const handleDeleteLesson = useCallback((chapterId, lesson) => {
    setConfirmAction({
      type: 'delete-lesson',
      payload: { chapterId, lessonId: lesson._id },
      message: `آیا از حذف درس "${lesson.title}" با ${lesson.steps?.length || 0} مرحله مطمئن هستید؟`,
    });
  }, []);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction) return;
    const { type, payload } = confirmAction;
    try {
      if (type === 'delete-chapter') {
        draft.removeChapter(payload.chapterId);
        toast?.('فصل حذف شد', 'success');
      } else if (type === 'delete-lesson') {
        draft.removeLesson(payload.chapterId, payload.lessonId);
        toast?.('درس حذف شد', 'success');
      } else if (type === 'delete-step') {
        draft.removeStep(payload.chapterId, payload.lessonId, payload.stepId);
        toast?.('مرحله حذف شد', 'success');
      }
    } finally {
      setConfirmAction(null);
    }
  }, [confirmAction, draft, toast]);

  const openLessonEditor = useCallback((chapterId, lessonId) => {
    setEditingLesson({ chapterId, lessonId });
  }, []);

  const closeLessonEditor = useCallback(() => {
    setEditingLesson(null);
    if (draft.hasUnsaved) draft.forceSave();
  }, [draft]);

  // 🆕 ذخیره تنظیمات اشتراک
  const handleSaveSubscription = useCallback((data) => {
    if (!project) return;
    const res = dataEngine.project.update(project.id, data);
    if (res?.ok) {
      setShowSubscriptionSettings(false);
      toast?.('تنظیمات اشتراک ذخیره شد ✨', 'success');
    } else {
      const msg = (res?.errors || ['خطای نامشخص'])[0];
      const map = {
        subscription_price_negative: 'قیمت نمی‌تواند منفی باشد',
        subscription_duration_out_of_range: 'مدت باید بین ۱ تا ۳۶۵۰ روز باشد',
        subscription_grace_out_of_range: 'مهلت اضافه باید بین ۰ تا ۳۰ روز باشد',
      };
      toast?.(map[msg] || msg, 'error');
    }
  }, [project, toast]);

  // ═══ همه‌ی useEffect ═══
  useEffect(() => {
    if (!project?.publishedVersionId || !draft.draft) {
      setDiff(null);
      return;
    }
    const pub = dataEngine.version.get(project.publishedVersionId);
    if (!pub) { setDiff(null); return; }
    try {
      const d = diffEngine.compare(pub, draft.draft);
      setDiff(d?.ok ? d : null);
    } catch (e) {
      setDiff(null);
    }
  }, [project?.publishedVersionId, draft.draft]);

  useEffect(() => {
    if (draft.draft?.chapters) {
      const open = {};
      draft.draft.chapters.forEach((ch) => { open[ch._id] = true; });
      setOpenChapters((prev) => {
        const merged = { ...open };
        for (const k of Object.keys(prev)) if (k in open) merged[k] = prev[k];
        return merged;
      });
    }
  }, [draft.draft?.chapters?.length]);

  // 🆕 ذخیره تم در کلید مشترک Navbar2
  // استفاده از engine.setRaw تا event `storage:write` صادر بشه
  // و remoteSync بتونه آن را در userPrefs (دیتابیس) sync کنه
  useEffect(() => {
    try {
      engine.setRaw('navbar2-drawer-dark', isDarkMode ? '1' : '0');
    } catch {}
  }, [isDarkMode]);

  // 🆕 گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'navbar2-drawer-dark') {
        setIsDarkMode(e.newValue === '1');
      }
    };
    window.addEventListener('storage', handleStorage);

    const interval = setInterval(() => {
      try {
        // 🆕 خواندن از engine (هماهنگ با remoteSync)
        const current = engine.getRaw('navbar2-drawer-dark') === '1';
        setIsDarkMode((prev) => (prev !== current ? current : prev));
      } catch {}
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  // ═══ hookهای سفارشی ═══
  useBeforeUnload(draft.hasUnsaved);
  useKeyboardShortcuts(shortcuts, { enabled: ready && isOwner });

  // ═══════════════════════════════════════════════════════
  // از اینجا به بعد: هیچ hookی نباید صدا زده شود
  // ═══════════════════════════════════════════════════════

  if (!ready) return <EditorLoading />;
  if (draft.loading) return <EditorLoading />;

  if (draft.error === 'project_not_found') {
    return (
      <div className="tpe-error-page">
        <EmptyState
          icon="📦"
          title="پروژه یافت نشد"
          description="این پروژه وجود ندارد یا حذف شده است."
          action={
            <button className="tpe-btn tpe-btn-primary" onClick={() => navigate('/teacher/books?tab=projects')}>
              بازگشت به پروژه‌ها
            </button>
          }
        />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="tpe-error-page">
        <EmptyState
          icon="🔐"
          title="دسترسی محدود"
          description="شما مالک این پروژه نیستید."
          action={
            <button className="tpe-btn tpe-btn-primary" onClick={() => navigate('/teacher/books')}>
              بازگشت
            </button>
          }
        />
      </div>
    );
  }

  const isLocked = draft.error === 'locked_pending_review' || project?.state === PROJECT_STATE.UPDATE_PENDING;
  const isPublished = !!project?.publishedVersionId;
  const chapters = draft.draft?.chapters || [];

  // 🆕 وضعیت اشتراک
  const subscriptionEnabled = !!project?.subscriptionEnabled && Number(project?.subscriptionPrice) > 0;
  const subscriptionPrice = Number(project?.subscriptionPrice) || 0;
  const subscriptionDurationDays = Number(project?.subscriptionDurationDays) || 365;

  return (
    <div className={`tpe-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <EditorBackground />

      <header className="tpe-header">
        <div className="tpe-header-inner">
          <div className="tpe-header-left">
            <button
              className="tpe-icon-btn"
              onClick={() => navigate('/teacher/books?tab=projects')}
              title="بازگشت"
            >
              <IconArrow style={{ transform: 'rotate(180deg)' }} />
            </button>
            <div className="tpe-header-title-group">
              <h1 className="tpe-title">
                {project?.title || `${SUBJECT_LABELS[project?.subject] || ''} — ${GRADE_TITLES[String(project?.grade)] || ''}`}
              </h1>
              <div className="tpe-header-meta">
                <ProjectStateBadge state={project?.state || PROJECT_STATE.BUILDING} size="sm" />
                {isPublished && <Badge tone="info" size="sm">نسخه‌ی منتشرشده فعال</Badge>}
                {/* 🆕 برچسب اشتراک */}
                {subscriptionEnabled && (
                  <Badge tone="purple" size="sm">
                    💰 {subscriptionPrice.toLocaleString('fa-IR')} تومان
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="tpe-header-center">
            <AutoSaveIndicator
              isSaving={draft.isSaving}
              hasUnsaved={draft.hasUnsaved}
              lastSavedAt={draft.lastSavedAt}
              onForceSave={draft.forceSave}
            />
          </div>

          <div className="tpe-header-right">
            <div className="tpe-btn-group">
              <button
                className="tpe-icon-btn"
                onClick={draft.undo}
                disabled={!draft.canUndo || isLocked}
                title="واگرد (Ctrl+Z)"
              >
                <IconUndo />
              </button>
              <button
                className="tpe-icon-btn"
                onClick={draft.redo}
                disabled={!draft.canRedo || isLocked}
                title="از نو (Ctrl+Shift+Z)"
              >
                <IconRedo />
              </button>
              <span className="tpe-divider" />
              <button
                className="tpe-icon-btn"
                onClick={draft.forceSave}
                disabled={!draft.hasUnsaved || draft.isSaving || isLocked}
                title="ذخیره (Ctrl+S)"
              >
                <IconSave />
              </button>
              <button
                className="tpe-btn tpe-btn-ghost"
                onClick={() => setShowPreview(true)}
                title="پیش‌نمایش"
              >
                <IconEye /> پیش‌نمایش
              </button>

              {!isLocked && (
                <button
                  className="tpe-btn tpe-btn-primary"
                  onClick={() => setShowSubmitModal(true)}
                  disabled={chapters.length === 0}
                >
                  <IconSend /> {isPublished ? 'درخواست بروزرسانی' : 'ارسال برای بررسی'}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {isLocked && (
        <LockBanner
          reason="نسخه‌ی جدید شما در حال بررسی توسط مدیر است. تا زمان تصمیم مدیر، امکان ویرایش وجود ندارد."
          icon="🔒"
        />
      )}

      {/* 🆕 نمایش دلیل رد یا درخواست اصلاح مدیر به معلم */}
      {!isLocked && (draft.draft?.rejectionReason || draft.draft?.adminNotes) && (
        <div
          className="tpe-change-banner"
          style={{
            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            borderColor: '#fca5a5',
            marginTop: 16,
          }}
        >
          <div className="tpe-change-info" style={{ color: '#991b1b' }}>
            <span className="tpe-change-icon">⚠️</span>
            <span style={{ fontWeight: 600 }}>
              <strong>پیام از طرف مدیر:</strong>{' '}
              {draft.draft.rejectionReason || draft.draft.adminNotes}
            </span>
          </div>
        </div>
      )}

      {/* 🆕 بنر تنظیمات اشتراک */}
      {!isLocked && (
        <div
          className="tpe-change-banner"
          style={{
            background: subscriptionEnabled
              ? 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
              : 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
            borderColor: subscriptionEnabled ? '#c4b5fd' : '#c7d2fe',
          }}
        >
          <div className="tpe-change-info">
            <span className="tpe-change-icon">{subscriptionEnabled ? '💰' : '🆓'}</span>
            <span style={{ fontWeight: 600 }}>
              {subscriptionEnabled
                ? `اشتراک فعال: ${subscriptionPrice.toLocaleString('fa-IR')} تومان / ${subscriptionDurationDays} روز`
                : 'سیستم اشتراک غیرفعال است — همه‌ی دروس رایگان‌اند'}
            </span>
          </div>
          <button
            className="tpe-btn tpe-btn-primary"
            onClick={() => setShowSubscriptionSettings(true)}
          >
            ⚙️ تنظیمات اشتراک
          </button>
        </div>
      )}

      {isPublished && diff && (diff.summary.addedCount + diff.summary.removedCount + diff.summary.modifiedCount + diff.summary.movedCount) > 0 && (
        <div className="tpe-change-banner">
          <div className="tpe-change-info">
            <span className="tpe-change-icon">✨</span>
            <span>
              <strong>
                {diff.summary.addedCount + diff.summary.removedCount + diff.summary.modifiedCount + diff.summary.movedCount} تغییر
              </strong>
              {' '}نسبت به نسخه‌ی منتشرشده
            </span>
          </div>
          <button className="tpe-btn tpe-btn-ghost" onClick={() => setShowDiffModal(true)}>
            مشاهده‌ی جزئیات
          </button>
        </div>
      )}

      <main className="tpe-main">
        {chapters.length === 0 ? (
          <div className="tpe-empty-chapters">
            <EmptyState
              icon="📚"
              title="هنوز فصلی نساخته‌اید"
              description="با افزودن اولین فصل، ساختار محتوای آموزشی خود را آغاز کنید."
              action={
                !isLocked && (
                  <button className="tpe-btn tpe-btn-primary tpe-btn-lg" onClick={() => setAddingChapter(true)}>
                    <IconPlus size={18} /> افزودن اولین فصل
                  </button>
                )
              }
            />
          </div>
        ) : (
          <>
            <div className="tpe-chapters">
              {chapters.map((chapter, chapterIdx) => (
                <ChapterSection
                  key={chapter._id}
                  chapter={chapter}
                  chapterIndex={chapterIdx}
                  chapterCount={chapters.length}
                  isOpen={!!openChapters[chapter._id]}
                  onToggle={() => toggleChapter(chapter._id)}
                  onRename={(title) => handleRenameChapter(chapter._id, title)}
                  onDelete={() => handleDeleteChapter(chapter)}
                  onMoveUp={() => draft.moveChapter(chapter._id, 'up')}
                  onMoveDown={() => draft.moveChapter(chapter._id, 'down')}
                  onAddLesson={() => handleAddLesson(chapter._id)}
                  onOpenLesson={(lessonId) => openLessonEditor(chapter._id, lessonId)}
                  onDeleteLesson={(lesson) => handleDeleteLesson(chapter._id, lesson)}
                  onMoveLesson={(lessonId, dir) => draft.moveLesson(chapter._id, lessonId, dir)}
                  locked={isLocked}
                  subscriptionEnabled={subscriptionEnabled}
                />
              ))}
            </div>

            {!isLocked && (
              <button
                className="tpe-add-chapter-btn"
                onClick={() => setAddingChapter(true)}
              >
                <IconPlus size={18} />
                افزودن فصل جدید
              </button>
            )}
          </>
        )}
      </main>

      {addingChapter && (
        <Modal
          isOpen
          onClose={() => { setAddingChapter(false); setNewChapterTitle(''); }}
          title="افزودن فصل جدید"
          size="sm"
          initialFocusSelector="input"
          footer={
            <>
              <button className="tpe-btn tpe-btn-ghost" onClick={() => { setAddingChapter(false); setNewChapterTitle(''); }}>
                انصراف
              </button>
              <button className="tpe-btn tpe-btn-primary" onClick={handleAddChapter}>
                <IconPlus size={14} /> افزودن
              </button>
            </>
          }
        >
          <div className="tpe-field">
            <label className="tpe-label">عنوان فصل</label>
            <input
              className="tpe-input"
              value={newChapterTitle}
              onChange={(e) => setNewChapterTitle(e.target.value)}
              placeholder="مثلاً: فصل اول — آشنایی با اعداد"
              maxLength={200}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddChapter(); }}
            />
            <div className="tpe-hint">{newChapterTitle.length} / 200</div>
          </div>
        </Modal>
      )}

      {/* 🆕 Modal نام درس */}
      {addingLessonForChapter && (
        <Modal
          isOpen
          onClose={() => { setAddingLessonForChapter(null); setNewLessonTitle(''); }}
          title="افزودن درس جدید به این فصل"
          size="sm"
          initialFocusSelector="input"
          footer={
            <>
              <button
                type="button"
                className="tpe-btn tpe-btn-ghost"
                onClick={() => { setAddingLessonForChapter(null); setNewLessonTitle(''); }}
              >
                انصراف
              </button>
              <button
                type="button"
                className="tpe-btn tpe-btn-primary"
                onClick={confirmAddLesson}
              >
                <IconPlus size={14} /> افزودن درس
              </button>
            </>
          }
        >
          <div className="tpe-field">
            <label className="tpe-label">نام درس</label>
            <input
              className="tpe-input"
              value={newLessonTitle}
              onChange={(e) => setNewLessonTitle(e.target.value)}
              placeholder="مثلاً: درس اول — آشنایی با اعداد"
              maxLength={200}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmAddLesson(); }}
            />
            <div className="tpe-hint">{newLessonTitle.length} / 200</div>
          </div>
          <div className="tpe-hint" style={{ marginTop: 12, lineHeight: 1.9 }}>
            💡 این درس داخل این فصل ساخته می‌شود. بعد از افزودن، روی کارت درس کلیک کنید تا مراحل آن را اضافه کنید.
          </div>
        </Modal>
      )}

      {/* 🆕 Modal نام مرحله (بعد از انتخاب نوع) */}
      {stepNameModal && (
        <Modal
          isOpen
          onClose={() => { setStepNameModal(null); setNewStepTitle(''); }}
          title={stepNameModal.type === 'quiz' ? 'ساخت آزمون جدید — نام آزمون' : 'ساخت مرحله جدید — نام مرحله'}
          size="sm"
          initialFocusSelector="input"
          footer={
            <>
              <button
                type="button"
                className="tpe-btn tpe-btn-ghost"
                onClick={() => { setStepNameModal(null); setNewStepTitle(''); }}
              >
                انصراف
              </button>
              <button
                type="button"
                className="tpe-btn tpe-btn-primary"
                onClick={confirmAddStep}
              >
                <IconPlus size={14} /> ایجاد
              </button>
            </>
          }
        >
          <div className="tpe-field">
            <label className="tpe-label">
              {stepNameModal.type === 'quiz' ? 'نام آزمون' : 'نام مرحله'}
            </label>
            <input
              className="tpe-input"
              value={newStepTitle}
              onChange={(e) => setNewStepTitle(e.target.value)}
              placeholder={
                stepNameModal.type === 'quiz'
                  ? 'مثلاً: آزمون پایان فصل'
                  : 'مثلاً: آشنایی با مفهوم'
              }
              maxLength={200}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmAddStep(); }}
            />
            <div className="tpe-hint">{newStepTitle.length} / 200</div>
          </div>
          <div className="tpe-hint" style={{ marginTop: 12, lineHeight: 1.9 }}>
            💡 این {stepNameModal.type === 'quiz' ? 'آزمون' : 'مرحله'} داخل درس فعلی ساخته می‌شود.
            {stepNameModal.type === 'quiz' && ' بعد از ایجاد، به ویرایشگر آزمون منتقل می‌شوید.'}
          </div>
        </Modal>
      )}

      {/* 🆕 Step Type Selector */}
      {stepTypeModal && (
        <StepTypeSelectorModal
          isOpen
          onClose={() => setStepTypeModal(null)}
          onSelect={handleStepTypeSelected}
        />
      )}

      {/* 🆕 Quiz Create Mode Selector */}
      {quizModeModal && (
        <QuizCreateModeModal
          isOpen
          onClose={() => setQuizModeModal(null)}
          onSelect={handleQuizModeSelected}
        />
      )}

      {editingLesson && draft.draft && (
        <LessonEditorModal
          chapterId={editingLesson.chapterId}
          lessonId={editingLesson.lessonId}
          draft={draft}
          locked={isLocked}
          subscriptionEnabled={subscriptionEnabled}
          onClose={closeLessonEditor}
          onDeleteStep={(step) => setConfirmAction({
            type: 'delete-step',
            payload: { chapterId: editingLesson.chapterId, lessonId: editingLesson.lessonId, stepId: step._id },
            message: `آیا از حذف مرحله‌ی "${step.title}" مطمئن هستید؟`,
          })}

          onAddStepClick={handleAddStepClick}
          onOpenQuizEditor={async (payload) => {
            const step = draft.draft?.chapters
              ?.find(c => c._id === payload.chapterId)
              ?.lessons?.find(l => l._id === payload.lessonId)
              ?.steps?.find(s => s._id === payload.stepId);

            if (step?.quizId) {
              try {
                await draft.forceSave();
              } catch (e) {
                console.error('[TeacherProjectEditor] forceSave before edit quiz failed:', e);
              }
              const qs = new URLSearchParams({
                chapterId: payload.chapterId,
                lessonId: payload.lessonId,
                stepId: payload.stepId,
                mode: 'edit',
              }).toString();
              navigate(`/teacher/books/edit/${projectId}/quiz/new?${qs}`);
            } else {
              setQuizModeModal(payload);
            }
          }}
        />
      )}

      {showPreview && draft.draft && (
        <PreviewModal
          draft={draft.draft}
          project={project}
          teacher={teacherProfile}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showDiffModal && diff && (
        <Modal
          isOpen
          onClose={() => setShowDiffModal(false)}
          title="تغییرات نسبت به نسخه‌ی منتشرشده"
          size="lg"
        >
          <DiffViewer diff={diff} />
        </Modal>
      )}

      {showSubmitModal && (
        <SubmitForReviewModal
          project={project}
          diff={diff}
          isPublished={isPublished}
          onClose={() => setShowSubmitModal(false)}
          onSuccess={() => {
            setShowSubmitModal(false);
            toast?.('درخواست شما ارسال شد', 'success');
            navigate('/teacher/books?tab=projects');
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
        title="تایید حذف"
        message={confirmAction?.message || ''}
        confirmText="حذف کن"
        tone="danger"
      />

      {/* 🆕 مودال تنظیمات اشتراک */}
      {showSubscriptionSettings && project && (
        <SubscriptionSettingsModal
          project={project}
          onClose={() => setShowSubscriptionSettings(false)}
          onSave={handleSaveSubscription}
        />
      )}
    </div>
  );
}

// ================================================================
// SECTION 4: BACKGROUND + LOADING
// ================================================================

function EditorBackground() {
  return (
    <div className="tpe-bg" aria-hidden="true">
      <div className="tpe-bg-orb tpe-bg-orb-1" />
      <div className="tpe-bg-orb tpe-bg-orb-2" />
      <div className="tpe-bg-grid" />
    </div>
  );
}

function EditorLoading() {
  // 🆕 خواندن از engine (هماهنگ با remoteSync)
  const [isDarkMode] = useState(() => {
    try { return engine.getRaw('navbar2-drawer-dark') === '1'; }
    catch { return false; }
  });

  return (
    <div className={`tpe-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <EditorBackground />
      <header className="tpe-header">
        <div className="tpe-header-inner">
          <SkeletonLoader variant="line" />
        </div>
      </header>
      <main className="tpe-main">
        <SkeletonLoader variant="card" count={3} />
      </main>
    </div>
  );
}

// ================================================================
// SECTION 5: CHAPTER SECTION
// ================================================================

function ChapterSection({
  chapter, chapterIndex, chapterCount, isOpen, onToggle,
  onRename, onDelete, onMoveUp, onMoveDown,
  onAddLesson, onOpenLesson, onDeleteLesson, onMoveLesson,
  locked,
  subscriptionEnabled,
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(chapter.title);
  const titleInputRef = useRef(null);

  useEffect(() => { setTitleDraft(chapter.title); }, [chapter.title]);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  const commitTitle = () => {
    const t = titleDraft.trim();
    if (t && t !== chapter.title) onRename(t);
    else setTitleDraft(chapter.title);
    setEditingTitle(false);
  };

  return (
    <section className={`tpe-chapter ${isOpen ? 'open' : ''}`}>
      <header className="tpe-chapter-header">
        <button className="tpe-chapter-toggle" onClick={onToggle} aria-expanded={isOpen}>
          <span className="tpe-chapter-arrow">
            <IconChevron dir={isOpen ? 'up' : 'down'} size={18} />
          </span>
          <span className="tpe-chapter-num">{chapterIndex + 1}</span>
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className="tpe-chapter-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
                if (e.key === 'Escape') { setTitleDraft(chapter.title); setEditingTitle(false); }
              }}
              onClick={(e) => e.stopPropagation()}
              maxLength={200}
            />
          ) : (
            <span className="tpe-chapter-title">{chapter.title}</span>
          )}
          <span className="tpe-chapter-count">
            {chapter.lessons?.length || 0} درس
          </span>
        </button>

        {!locked && (
          <div className="tpe-chapter-actions">
            <button
              className="tpe-icon-btn tpe-icon-btn-sm"
              onClick={() => setEditingTitle(true)}
              title="ویرایش عنوان"
            >
              <IconEdit size={14} />
            </button>
            <button
              className="tpe-icon-btn tpe-icon-btn-sm"
              onClick={onMoveUp}
              disabled={chapterIndex === 0}
              title="انتقال به بالا"
            >
              <IconUp />
            </button>
            <button
              className="tpe-icon-btn tpe-icon-btn-sm"
              onClick={onMoveDown}
              disabled={chapterIndex === chapterCount - 1}
              title="انتقال به پایین"
            >
              <IconDown />
            </button>
            <button
              className="tpe-icon-btn tpe-icon-btn-sm tpe-icon-btn-danger"
              onClick={onDelete}
              title="حذف فصل"
            >
              <IconTrash size={14} />
            </button>
          </div>
        )}
      </header>

      {isOpen && (
        <div className="tpe-chapter-body">
          {chapter.lessons?.length === 0 ? (
            <div className="tpe-empty-lessons">
              <span>این فصل هنوز درسی ندارد.</span>
              {!locked && (
                <button className="tpe-btn tpe-btn-ghost" onClick={onAddLesson}>
                  <IconPlus size={14} /> افزودن اولین درس به این فصل
                </button>
              )}
            </div>
          ) : (
            <div className="tpe-lessons-grid">
              {chapter.lessons.map((lesson, lessonIdx) => (
                <LessonCard
                  key={lesson._id}
                  lesson={lesson}
                  lessonIndex={lessonIdx}
                  lessonCount={chapter.lessons.length}
                  onOpen={() => onOpenLesson(lesson._id)}
                  onDelete={() => onDeleteLesson(lesson)}
                  onMoveUp={() => onMoveLesson(lesson._id, 'up')}
                  onMoveDown={() => onMoveLesson(lesson._id, 'down')}
                  locked={locked}
                  subscriptionEnabled={subscriptionEnabled}
                />
              ))}
            </div>
          )}

          {!locked && chapter.lessons?.length > 0 && (
            <button className="tpe-add-lesson-btn" onClick={onAddLesson}>
              <IconPlus size={16} /> افزودن درس جدید به این فصل
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ================================================================
// SECTION 6: LESSON CARD
// ================================================================

function LessonCard({
  lesson, lessonIndex, lessonCount, onOpen, onDelete, onMoveUp, onMoveDown,
  locked, subscriptionEnabled,
}) {
  const stepsCount = lesson.steps?.length || 0;
  const videosCount = useMemo(
    () => (lesson.steps || []).reduce((sum, s) => sum + (s.videos?.length || 0), 0),
    [lesson.steps]
  );

  // 🆕 آیا این درس رایگان است؟
  const showFreeBadge = subscriptionEnabled && lesson.isFree === true;

  return (
    <article className="tpe-lesson-card" onClick={locked ? null : onOpen}>
      <div className="tpe-lesson-thumb">
        {lesson.imageBlobId ? (
          <BlobImage blobId={lesson.imageBlobId} alt={lesson.title} />
        ) : (
          <div className="tpe-lesson-thumb-placeholder">
            <span>📘</span>
          </div>
        )}
        {locked && <div className="tpe-lesson-lock">🔒</div>}
        {/* 🆕 برچسب رایگان */}
        {showFreeBadge && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: '#10b981',
              color: '#ffffff',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 800,
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
            }}
          >
            🎁 رایگان
          </div>
        )}
      </div>
      <div className="tpe-lesson-body">
        <h4 className="tpe-lesson-title">{lesson.title}</h4>
        {lesson.description && (
          <p className="tpe-lesson-desc">{lesson.description.slice(0, 80)}{lesson.description.length > 80 ? '…' : ''}</p>
        )}
        <div className="tpe-lesson-meta">
          <span className="tpe-lesson-pill">
            <span className="tpe-pill-icon">🎯</span>
            {stepsCount} مرحله
          </span>
          {videosCount > 0 && (
            <span className="tpe-lesson-pill">
              <span className="tpe-pill-icon">🎬</span>
              {videosCount} ویدیو
            </span>
          )}
        </div>
      </div>
      {!locked && (
        <div className="tpe-lesson-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="tpe-icon-btn tpe-icon-btn-sm"
            onClick={onMoveUp}
            disabled={lessonIndex === 0}
            title="بالا"
          >
            <IconUp />
          </button>
          <button
            className="tpe-icon-btn tpe-icon-btn-sm"
            onClick={onMoveDown}
            disabled={lessonIndex === lessonCount - 1}
            title="پایین"
          >
            <IconDown />
          </button>
          <button
            className="tpe-icon-btn tpe-icon-btn-sm tpe-icon-btn-danger"
            onClick={onDelete}
            title="حذف درس"
          >
            <IconTrash size={14} />
          </button>
        </div>
      )}
    </article>
  );
}

// ================================================================
// SECTION 7: LESSON EDITOR MODAL
// ================================================================

function LessonEditorModal({
  chapterId, lessonId, draft, locked, onClose, onDeleteStep,
  onAddStepClick,
  onOpenQuizEditor,
  subscriptionEnabled,
}) {
  const lesson = useMemo(() => {
    const ch = draft.draft?.chapters?.find((c) => c._id === chapterId);
    return ch?.lessons?.find((l) => l._id === lessonId);
  }, [draft.draft, chapterId, lessonId]);

  if (!lesson) return null;

  const updateLessonField = (field, value) => {
    draft.updateLesson(chapterId, lessonId, { [field]: value });
  };

  const updateStep = (stepId, patch) => {
    draft.updateStep(chapterId, lessonId, stepId, patch);
  };

  return (
    <div className="tpe-lesson-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tpe-lesson-modal">
        <header className="tpe-lesson-modal-header">
          <div className="tpe-lesson-modal-title-group">
            <span className="tpe-lesson-modal-label">ویرایش درس</span>
            <input
              className="tpe-lesson-modal-title-input"
              value={lesson.title}
              onChange={(e) => updateLessonField('title', e.target.value)}
              maxLength={200}
              disabled={locked}
              placeholder="عنوان درس..."
            />
          </div>
          <div className="tpe-lesson-modal-actions">
            {!locked && (
              <button
                className="tpe-btn tpe-btn-primary"
                onClick={() => onAddStepClick(chapterId, lessonId)}
              >
                <IconPlus size={14} /> افزودن مرحله
              </button>
            )}
            <button className="tpe-icon-btn" onClick={onClose} title="بستن">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </header>

        <div className="tpe-lesson-modal-body">
          {/* اطلاعات پایه درس */}
          <section className="tpe-lesson-section">
            <h3 className="tpe-section-label">اطلاعات صفحه ورود به مراحل</h3>
            <div className="tpe-lesson-fields">
              <div className="tpe-field">
                <label className="tpe-label">توضیح درس</label>
                <textarea
                  className="tpe-textarea"
                  rows={2}
                  value={lesson.description || ''}
                  onChange={(e) => updateLessonField('description', e.target.value)}
                  placeholder="توضیح مختصری از این درس..."
                  maxLength={5000}
                  disabled={locked}
                />
              </div>
              <div className="tpe-field">
                <label className="tpe-label">تصویر درس</label>
                {locked ? (
                  lesson.imageBlobId ? (
                    <BlobImage blobId={lesson.imageBlobId} alt={lesson.title} style={{ maxWidth: 260, borderRadius: 16 }} />
                  ) : (
                    <div className="tpe-hint">بدون تصویر</div>
                  )
                ) : (
                  <ImageUploader
                    value={lesson.imageBlobId}
                    onChange={(blobId) => updateLessonField('imageBlobId', blobId)}
                    label=""
                    aspect="16/9"
                    refType="image"
                    hint="حداکثر ۵ مگابایت"
                  />
                )}
              </div>

              {/* 🆕 چک‌باکس درس رایگان — فقط اگر اشتراک فعال باشد */}
              {subscriptionEnabled && (
                <div className="tpe-field" style={{ gridColumn: '1 / -1' }}>
                  <label
                    className="tpe-label"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: locked ? 'not-allowed' : 'pointer',
                      padding: '10px 14px',
                      background: lesson.isFree ? '#d1fae5' : '#f9fafb',
                      borderRadius: 10,
                      border: lesson.isFree ? '1px solid #6ee7b7' : '1px solid #e5e7eb',
                      transition: 'all 0.2s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!lesson.isFree}
                      onChange={(e) => updateLessonField('isFree', e.target.checked)}
                      disabled={locked}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: lesson.isFree ? '#065f46' : '#1f2937',
                        }}
                      >
                        🎁 این درس برای همه رایگان است
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#6b7280',
                          marginTop: 2,
                        }}
                      >
                        اگر فعال باشد، این درس بدون نیاز به اشتراک برای همه‌ی دانش‌آموزان باز خواهد بود.
                      </div>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </section>

          <div className="tpe-divider" />

          {/* مراحل */}
          <section className="tpe-lesson-section tpe-lesson-section1">
            <h3 className="tpe-section-label">
              مراحل آموزشی
              <span className="tpe-section-count">{lesson.steps?.length || 0} مرحله</span>
            </h3>

            {(!lesson.steps || lesson.steps.length === 0) ? (
              <EmptyState
                icon="🎯"
                title="هنوز مرحله‌ای نساخته‌اید"
                description="هر درس شامل مراحل پشت سر هم است. هر مرحله می‌تواند متن و ویدیو داشته باشد."
                size="sm"
                action={
                  !locked && (
                    <button
                      className="tpe-btn tpe-btn-primary"
                      onClick={() => onAddStepClick(chapterId, lessonId)}
                    >
                      <IconPlus size={14} /> افزودن مرحله
                    </button>
                  )
                }
              />
            ) : (
              <>
                <div className="tpe-steps-list">
                  {lesson.steps.map((step, idx) => (
                    <StepEditor
                      key={step._id}
                      step={step}
                      index={idx}
                      total={lesson.steps.length}
                      locked={locked}
                      onChange={(patch) => updateStep(step._id, patch)}
                      onDelete={() => onDeleteStep(step)}
                      onMoveUp={() => draft.moveStep(chapterId, lessonId, step._id, 'up')}
                      onMoveDown={() => draft.moveStep(chapterId, lessonId, step._id, 'down')}
                      onAddVideo={(videoRef) => draft.addVideo(chapterId, lessonId, step._id, videoRef)}
                      onRemoveVideo={(videoId) => draft.removeVideo(chapterId, lessonId, step._id, videoId)}
                      chapterId={chapterId}
                      lessonId={lessonId}
                      onOpenQuizEditor={onOpenQuizEditor}
                    />
                  ))}
                </div>
                {/* 🆕 دکمه‌ی افزودن مرحله در پایین لیست */}
                {!locked && (
                  <button
                    type="button"
                    className="tpe-add-lesson-btn"
                    onClick={() => onAddStepClick(chapterId, lessonId)}
                    style={{ marginTop: 16, width: '100%' }}
                  >
                    <IconPlus size={16} /> افزودن مرحله‌ی جدید به این درس
                  </button>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 8: STEP EDITOR
// ================================================================

function StepEditor({
  step, index, total, locked, onChange, onDelete, onMoveUp, onMoveDown,
  onAddVideo, onRemoveVideo,
  chapterId, lessonId, onOpenQuizEditor,
}) {
  const isQuiz = step.type === 'quiz';
  const [expanded, setExpanded] = useState(!isQuiz);

  // 🆕 state محلی برای ورودی مدت — حل کامل مشکل تایپ عدد
  const [durationText, setDurationText] = useState(String(step.durationMinutes ?? 0));

  // 🆕 همگام‌سازی با مقدار بیرونی (فقط وقتی تغییر واقعی از بیرون بیاید)
  useEffect(() => {
    setDurationText(String(step.durationMinutes ?? 0));
  }, [step.durationMinutes]);

  // 🆕 تبدیل ارقام فارسی/عربی به لاتین + حذف کاراکترهای غیرمجاز
  const normalizeDigits = (s) =>
    String(s ?? '')
      .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
      .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
      .replace(/[^0-9]/g, '');

  // 🆕 تایید مقدار در blur یا Enter — همراه با clamp و اصلاح نمایش
  const commitDuration = (raw) => {
    const normalized = normalizeDigits(raw);
    if (normalized === '') {
      setDurationText('0');
      if (step.durationMinutes !== 0) onChange({ durationMinutes: 0 });
      return;
    }
    const num = parseInt(normalized, 10);
    const clamped = Math.max(0, Math.min(1440, isNaN(num) ? 0 : num));
    setDurationText(String(clamped));
    if (step.durationMinutes !== clamped) onChange({ durationMinutes: clamped });
  };

  if (isQuiz) {
    return (
      <div className={`tpe-step tpe-step-quiz ${expanded ? 'expanded' : 'collapsed'}`}>
        <header className="tpe-step-header">
          <button
            className="tpe-step-toggle"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
          >
            <span className="tpe-step-num">{index + 1}</span>
            <span className="tpe-step-quiz-badge">📝 آزمون</span>
            <span className="tpe-step-title">
              {step.title || 'آزمون بی‌نام'}
            </span>
            <span className="tpe-step-badges">
              {step.quizMeta?.questionCount > 0 && (
                <span className="tpe-step-pill">📊 {step.quizMeta.questionCount} سوال</span>
              )}
              {step.quizMeta?.duration > 0 && (
                <span className="tpe-step-pill">⏱️ {step.quizMeta.duration} دقیقه</span>
              )}
            </span>
            <IconChevron dir={expanded ? 'up' : 'down'} size={16} />
          </button>
          {!locked && (
            <div className="tpe-step-actions">
              <button
                className="tpe-icon-btn tpe-icon-btn-sm"
                onClick={onMoveUp}
                disabled={index === 0}
                title="بالا"
              >
                <IconUp />
              </button>
              <button
                className="tpe-icon-btn tpe-icon-btn-sm"
                onClick={onMoveDown}
                disabled={index === total - 1}
                title="پایین"
              >
                <IconDown />
              </button>
              <button
                className="tpe-icon-btn tpe-icon-btn-sm tpe-icon-btn-danger"
                onClick={onDelete}
                title="حذف"
              >
                <IconTrash size={14} />
              </button>
            </div>
          )}
        </header>

        {expanded && (
          <div className="tpe-step-body">
            <div className="tpe-field">
              <label className="tpe-label">عنوان مرحله آزمون</label>
              <input
                className="tpe-input"
                value={step.title || ''}
                onChange={(e) => onChange({ title: e.target.value })}
                maxLength={200}
                disabled={locked}
                placeholder="مثلاً: آزمون پایان فصل"
              />
            </div>

            {step.quizId ? (
              <div className="tpe-quiz-summary">
                <div className="tpe-quiz-stats">
                  <div className="tpe-quiz-stat">
                    <span className="tpe-quiz-stat-icon">📊</span>
                    <span>{step.quizMeta?.questionCount || 0} سوال</span>
                  </div>
                  <div className="tpe-quiz-stat">
                    <span className="tpe-quiz-stat-icon">⏱️</span>
                    <span>{step.quizMeta?.duration || 0} دقیقه</span>
                  </div>
                  <div className="tpe-quiz-stat">
                    <span className="tpe-quiz-stat-icon">🎯</span>
                    <span>قبولی: {step.quizMeta?.passThreshold || 12} از ۲۰</span>
                  </div>
                  <div className="tpe-quiz-stat">
                    <span className="tpe-quiz-stat-icon">🔄</span>
                    <span>{step.quizMeta?.maxAttempts || 2} تلاش مجاز</span>
                  </div>
                  <div className="tpe-quiz-stat">
                    <span className="tpe-quiz-stat-icon">💰</span>
                    <span>{(step.quizMeta?.retryPrice || 10000).toLocaleString('fa-IR')} تومان برای تلاش مجدد</span>
                  </div>
                </div>
                {!locked && (
                  <button
                    type="button"
                    className="tpe-btn tpe-btn-primary"
                    onClick={() => onOpenQuizEditor({ chapterId, lessonId, stepId: step._id })}
                  >
                    ✏️ ویرایش آزمون
                  </button>
                )}
              </div>
            ) : (
              <div className="tpe-quiz-empty">
                <p>⚠️ این آزمون هنوز ساخته نشده است.</p>
                {!locked && (
                  <button
                    type="button"
                    className="tpe-btn tpe-btn-primary"
                    onClick={() => onOpenQuizEditor({ chapterId, lessonId, stepId: step._id })}
                  >
                    🚀 ساخت آزمون
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`tpe-step ${expanded ? 'expanded' : 'collapsed'}`}>
      <header className="tpe-step-header">
        <button
          className="tpe-step-toggle"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
        >
          <span className="tpe-step-num">{index + 1}</span>
          <span className="tpe-step-title">
            {step.title || 'مرحله بی‌نام'}
          </span>
          <span className="tpe-step-badges">
            {step.durationMinutes > 0 && (
              <span className="tpe-step-pill">⏱️ {step.durationMinutes} دقیقه</span>
            )}
            {step.videos?.length > 0 && (
              <span className="tpe-step-pill">🎬 {step.videos.length}</span>
            )}
          </span>
          <IconChevron dir={expanded ? 'up' : 'down'} size={16} />
        </button>
        {!locked && (
          <div className="tpe-step-actions">
            <button
              className="tpe-icon-btn tpe-icon-btn-sm"
              onClick={onMoveUp}
              disabled={index === 0}
              title="بالا"
            >
              <IconUp />
            </button>
            <button
              className="tpe-icon-btn tpe-icon-btn-sm"
              onClick={onMoveDown}
              disabled={index === total - 1}
              title="پایین"
            >
              <IconDown />
            </button>
            <button
              className="tpe-icon-btn tpe-icon-btn-sm tpe-icon-btn-danger"
              onClick={onDelete}
              title="حذف"
            >
              <IconTrash size={14} />
            </button>
          </div>
        )}
      </header>

      {expanded && (
        <div className="tpe-step-body">
          <div className="tpe-step-row">
            <div className="tpe-field tpe-field-grow">
              <label className="tpe-label">عنوان مرحله</label>
              <input
                className="tpe-input"
                value={step.title || ''}
                onChange={(e) => onChange({ title: e.target.value })}
                maxLength={200}
                disabled={locked}
                placeholder="مثلاً: آشنایی با مفهوم"
              />
            </div>
            <div className="tpe-field tpe-field-narrow">
              <label className="tpe-label">مدت (دقیقه)</label>
              {/* 🆕 ورودی عددی مقاوم — پشتیبانی از ارقام فارسی، خالی‌کردن، clamp */}
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                dir="ltr"
                className="tpe-input"
                value={durationText}
                placeholder="0"
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setDurationText(val);
                  if (val !== '') {
                    const num = parseInt(val, 10);
                    if (!isNaN(num) && num >= 0 && num <= 1440 && step.durationMinutes !== num) {
                      onChange({ durationMinutes: num });
                    }
                  }
                }}
                onBlur={() => commitDuration(durationText)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                disabled={locked}
              />
            </div>
          </div>

          <div className="tpe-field">
            <label className="tpe-label">توضیح (متن بالای مرحله)</label>
            <textarea
              className="tpe-textarea"
              rows={3}
              value={step.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder="متنی که دانش‌آموز بالای این مرحله می‌بیند..."
              maxLength={5000}
              disabled={locked}
            />
            <div className="tpe-hint">{(step.description || '').length} / 5000</div>
          </div>

          <div className="tpe-field">
            <label className="tpe-label">
              <IconVideo size={16} /> ویدیوهای این مرحله
            </label>
            {locked ? (
              step.videos?.length > 0 ? (
                <div className="tpe-video-preview-list">
                  {step.videos.map((v) => (
                    <VideoPreview
                      key={v._id}
                      blobId={v.blobId}
                      externalUrl={v.externalUrl}
                      title={v.title}
                    />
                  ))}
                </div>
              ) : (
                <div className="tpe-hint">بدون ویدیو</div>
              )
            ) : (
              <VideoUploader
                videos={step.videos || []}
                onAdd={onAddVideo}
                onRemove={(v) => onRemoveVideo(v._id)}
                maxSizeMB={200}
                hint="فرمت‌های مجاز: MP4, WebM — حداکثر ۲۰۰ مگابایت"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 9: PREVIEW MODAL
// ================================================================

function PreviewModal({ draft, project, teacher, onClose }) {
  const [activeLesson, setActiveLesson] = useState(null);

  const chapters = draft.chapters || [];
  const subscriptionEnabled = !!project?.subscriptionEnabled && Number(project?.subscriptionPrice) > 0;

  return (
    <div className="tpe-preview-overlay">
      <div className="tpe-preview-banner">
        <span>👁 این یک پیش‌نمایش است — دانش‌آموزان این نسخه را نمی‌بینند</span>
        <button className="tpe-btn tpe-btn-ghost" onClick={onClose}>
          بستن پیش‌نمایش
        </button>
      </div>

      <div className="tpe-preview-scroll">
        <div className="tpe-preview-hero">
          <div className="tpe-preview-hero-cover">
            {project?.coverImageBlobId ? (
              <BlobImage blobId={project.coverImageBlobId} alt={project.title} />
            ) : (
              <div className="tpe-preview-hero-placeholder">📖</div>
            )}
          </div>
          <div className="tpe-preview-hero-info">
            <h1>{project?.title}</h1>
            <p className="tpe-preview-hero-sub">
              {GRADE_TITLES[String(project?.grade)]} · {SUBJECT_LABELS[project?.subject]}
            </p>
            <p className="tpe-preview-teacher">
              معلم: {teacher?.fullName || '—'}
            </p>
            {/* 🆕 نمایش وضعیت اشتراک در پیش‌نمایش */}
            {subscriptionEnabled && (
              <p className="tpe-preview-hero-sub" style={{ marginTop: 6 }}>
                💰 اشتراک: {Number(project.subscriptionPrice).toLocaleString('fa-IR')} تومان / {Number(project.subscriptionDurationDays) || 365} روز
              </p>
            )}
          </div>
        </div>

        {chapters.length === 0 ? (
          <EmptyState icon="📚" title="محتوایی برای نمایش نیست" />
        ) : (
          chapters.map((ch, idx) => (
            <section key={ch._id} className="tpe-preview-chapter">
              <h2 className="tpe-preview-chapter-title">
                <span className="tpe-preview-ch-num">{idx + 1}</span>
                {ch.title}
              </h2>
              <div className="tpe-preview-lessons">
                {ch.lessons.map((ls) => (
                  <button
                    key={ls._id}
                    className="tpe-preview-lesson-card"
                    onClick={() => setActiveLesson(ls)}
                  >
                    <div className="tpe-preview-lesson-img">
                      {ls.imageBlobId ? (
                        <BlobImage blobId={ls.imageBlobId} alt={ls.title} />
                      ) : (
                        <div className="tpe-preview-lesson-img-ph">📘</div>
                      )}
                      {/* 🆕 نشانگر رایگان در پیش‌نمایش */}
                      {subscriptionEnabled && ls.isFree === true && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            background: '#10b981',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 800,
                          }}
                        >
                          🎁
                        </div>
                      )}
                    </div>
                    <div className="tpe-preview-lesson-body">
                      <h4>{ls.title}</h4>
                      <span className="tpe-preview-lesson-meta">
                        🎯 {ls.steps?.length || 0} مرحله
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {activeLesson && (
        <div className="tpe-preview-lesson-detail" onClick={(e) => e.target === e.currentTarget && setActiveLesson(null)}>
          <div className="tpe-preview-lesson-detail-card">
            <header>
              <h3>{activeLesson.title}</h3>
              <button className="tpe-icon-btn" onClick={() => setActiveLesson(null)}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </header>
            <div className="tpe-preview-steps">
              {activeLesson.steps?.length === 0 ? (
                <EmptyState icon="🎯" title="بدون مرحله" size="sm" />
              ) : (
                activeLesson.steps?.map((st, i) => (
                  <div key={st._id} className="tpe-preview-step">
                    <span className="tpe-preview-step-num">{i + 1}</span>
                    <div>
                      <h5>{st.title}</h5>
                      {st.description && <p>{st.description}</p>}
                      {st.videos?.length > 0 && (
                        <div className="tpe-preview-step-videos">
                          {st.videos.map((v) => (
                            <VideoPreview key={v._id} blobId={v.blobId} externalUrl={v.externalUrl} title={v.title} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 10: SUBMIT FOR REVIEW MODAL
// ================================================================

function SubmitForReviewModal({ project, diff, isPublished, onClose, onSuccess }) {
  const { submitForReview } = useVersionActions();
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);

  const changes = diff
    ? diff.summary.addedCount + diff.summary.removedCount + diff.summary.modifiedCount + diff.summary.movedCount
    : 0;

  const handleSubmit = async () => {
    const res = await submitForReview.run(project.id, { note, changelog: [] });
    if (res?.ok) {
      onSuccess?.();
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isPublished ? 'ارسال درخواست بروزرسانی' : 'ارسال برای بررسی اولیه'}
      size="md"
      closeOnBackdrop={!submitForReview.loading}
      footer={
        <>
          <button className="tpe-btn tpe-btn-ghost" onClick={onClose} disabled={submitForReview.loading}>
            انصراف
          </button>
          <button
            className="tpe-btn tpe-btn-primary"
            onClick={handleSubmit}
            disabled={submitForReview.loading || (isPublished && !ack)}
          >
            {submitForReview.loading ? <Spinner size={14} /> : <IconSend size={14} />}
            {isPublished ? 'ارسال درخواست بروزرسانی' : 'ارسال برای بررسی'}
          </button>
        </>
      }
    >
      <div className="tpe-submit-body">
        {isPublished ? (
          <>
            <div className="tpe-submit-info">
              <div className="tpe-submit-icon">🔄</div>
              <p>
                شما در حال ارسال درخواست بروزرسانی هستید.
                تا زمان بررسی مدیر، دانش‌آموزان همچنان نسخه‌ی فعلی را می‌بینند.
              </p>
            </div>

            {changes > 0 && (
              <div className="tpe-submit-diff-summary">
                <div className="tpe-submit-diff-title">خلاصه‌ی تغییرات:</div>
                <div className="tpe-submit-diff-stats">
                  <span className="tpe-submit-stat add">➕ {diff.summary.addedCount} اضافه</span>
                  <span className="tpe-submit-stat mod">✏️ {diff.summary.modifiedCount} تغییر</span>
                  <span className="tpe-submit-stat del">🗑 {diff.summary.removedCount} حذف</span>
                  <span className="tpe-submit-stat mov">↔ {diff.summary.movedCount} جابه‌جایی</span>
                </div>
              </div>
            )}

            {diff?.summary.removedCount > 0 && (
              <div className="tpe-submit-warning">
                ⚠️ حذف بعضی مراحل باعث می‌شود پیشرفت دانش‌آموزان در آن مراحل آرشیو شود
                (پاک نمی‌شود اما در نمایش فعلی نخواهد بود).
              </div>
            )}

            <div className="tpe-field">
              <label className="tpe-label">توضیح برای مدیر (اختیاری)</label>
              <textarea
                className="tpe-textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="مثلاً: ویدیوها را بهتر کردم و درس جدید اضافه کردم..."
                maxLength={1000}
              />
              <div className="tpe-hint">{note.length} / 1000</div>
            </div>

            <label className="tpe-checkbox">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              <span>از ارسال درخواست مطمئنم و می‌دانم تا بررسی مدیر نمی‌توانم ویرایش کنم.</span>
            </label>
          </>
        ) : (
          <>
            <div className="tpe-submit-info">
              <div className="tpe-submit-icon">📤</div>
              <p>
                درخواست شما برای تایید به مدیر ارسال می‌شود.
                پس از تایید، پروژه برای دانش‌آموزان قابل مشاهده خواهد شد.
              </p>
            </div>

            <div className="tpe-field">
              <label className="tpe-label">توضیح برای مدیر (اختیاری)</label>
              <textarea
                className="tpe-textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="هر توضیحی که فکر می‌کنید به مدیر کمک می‌کند..."
                maxLength={1000}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ================================================================
// SECTION 11: 🆕 SUBSCRIPTION SETTINGS MODAL
// ================================================================

function SubscriptionSettingsModal({ project, onClose, onSave }) {
  const [enabled, setEnabled] = useState(
    !!project.subscriptionEnabled || Number(project.subscriptionPrice) > 0
  );
  const [price, setPrice] = useState(Number(project.subscriptionPrice) || 0);
  const [durationDays, setDurationDays] = useState(
    Number(project.subscriptionDurationDays) || 365
  );
  const [graceDays, setGraceDays] = useState(
    Number(project.subscriptionGraceDays) || 3
  );

  const handleSave = () => {
    const numPrice = Math.max(0, Math.floor(Number(price) || 0));
    const numDur = Math.max(1, Math.min(3650, Math.floor(Number(durationDays) || 365)));
    const numGrace = Math.max(0, Math.min(30, Math.floor(Number(graceDays) || 3)));

    onSave({
      subscriptionEnabled: enabled && numPrice > 0,
      subscriptionPrice: numPrice,
      subscriptionDurationDays: numDur,
      subscriptionGraceDays: numGrace,
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="💰 تنظیمات اشتراک"
      size="md"
      footer={
        <>
          <button className="tpe-btn tpe-btn-ghost" onClick={onClose}>
            انصراف
          </button>
          <button className="tpe-btn tpe-btn-primary" onClick={handleSave}>
            💾 ذخیره
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 12,
            background: '#f9fafb',
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <div>
            <div style={{ fontWeight: 700, color: '#1f2937' }}>
              فعال‌سازی سیستم اشتراک
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              دانش‌آموزان باید قبل از دسترسی، هزینه را پرداخت کنند
            </div>
          </div>
        </label>

        {enabled && (
          <>
                       <div className="tpe-field">
              <label className="tpe-label">قیمت اشتراک (تومان)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="tpe-input"
                value={price || ''}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setPrice(val ? Number(val) : 0);
                }}
                placeholder="مثال: 50000"
              />
              {/* ... */}
            </div>

            <div className="tpe-field">
              <label className="tpe-label">مدت اشتراک (روز)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="tpe-input"
                value={durationDays || ''}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setDurationDays(val ? Number(val) : 1);
                }}
              />
            </div>

            <div className="tpe-field">
              <label className="tpe-label">مهلت اضافه بعد از انقضا (روز)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="tpe-input"
                value={graceDays || ''}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setGraceDays(val ? Number(val) : 0);
                }}
              />
            </div>
          </>
        )}

        <div
          style={{
            padding: 12,
            background: '#fef3c7',
            borderRadius: 10,
            fontSize: 13,
            color: '#92400e',
          }}
        >
          ℹ️ تغییرات این تنظیمات روی خریدهای آینده تأثیر می‌گذارد.
          اشتراک‌های فعلی تا زمان انقضا معتبر باقی می‌مانند.
        </div>

        <div
          style={{
            padding: 12,
            background: '#eef2ff',
            borderRadius: 10,
            fontSize: 13,
            color: '#3730a3',
          }}
        >
          💡 برای اینکه بعضی دروس رایگان باشند، در ویرایش هر درس تیک «این درس برای همه رایگان است» را بزنید.
        </div>
      </div>
    </Modal>
  );
}